import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FacilityDobryAgentService } from '../../services/facility-dobry-agent.service';
import { FacilityDobryAgent } from '../../interface/facility-dobry-agent';
import { ListResponse } from '../../interface/response/list-response';

@Component({
  selector: 'app-facility-dobry-agent-select',
  templateUrl: './facility-dobry-agent-select.component.html',
  styleUrls: ['./facility-dobry-agent-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FacilityDobryAgentSelectComponent),
    },
  ],
})
export class FacilityDobryAgentSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Placówka DobryAgent';
  @Input() returnKey = false;
  @Input() multiple = false;
  @Input() control: FormControl | null = null;
  @Input() required = false;
  @Input() onlyUnlinked = false;
  @Input() disable = false;

  externalControl = true;
  disabled = false;

  facilities: FacilityDobryAgent[] = [];
  filteredFacilities: FacilityDobryAgent[] | null = null;

  filterControl: FormControl;

  value?: FacilityDobryAgent | FacilityDobryAgent[] | string | string[] | null = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private facilityDobryAgentService: FacilityDobryAgentService,
    private fb: FormBuilder,
  ) {
    this.filterControl = this.fb.control('');
  }

  ngOnInit(): void {
    /**
     * Ważne:
     * parent może przekazać undefined, dlatego nie sprawdzamy tylko === null.
     */
    if (!this.control) {
      this.control = new FormControl({
        value: this.multiple ? [] : '',
        disabled: this.disable,
      });
      this.externalControl = false;
    } else if (this.disable) {
      this.control.disable();
    }

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val?: string) => {
        if (!val) {
          this.filteredFacilities = null;
          return;
        }

        const normalizedValue = this.normalizeSearchValue(val);

        this.filteredFacilities = this.facilities.filter((facility: FacilityDobryAgent) => {
          const searchable = this.normalizeSearchValue([
            facility.name,
            facility.asf24AddressUuid,
            this.getAddressLabel(facility),
            facility.address.street,
            facility.address.houseNumber,
            facility.address.zipCode,
            typeof facility.address.city === 'string'
              ? facility.address.city
              : (facility as any).city?.name,
          ].filter(Boolean).join(' '));

          return searchable.includes(normalizedValue);
        });
      });

    this.facilityDobryAgentService
      .getFacilityDobryAgentListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<FacilityDobryAgent>) => {
        this.facilities = this.resolveFacilities(result);
      });

    this.facilityDobryAgentService.loadList({
      onlyUnlinked: this.onlyUnlinked,
      disableCount: true,
    });
  }

  private resolveFacilities(result: ListResponse<FacilityDobryAgent>): FacilityDobryAgent[] {
    if (!result) {
      return [];
    }

    if (Array.isArray(result)) {
      return result as unknown as FacilityDobryAgent[];
    }

    if (Array.isArray((result as any).items)) {
      return (result as any).items;
    }

    if (Array.isArray((result as any).data)) {
      return (result as any).data;
    }

    if (Array.isArray((result as any).list)) {
      return (result as any).list;
    }

    return [];
  }

  private normalizeSearchValue(value: string | null | undefined): string {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l')
      .replace(/ą/g, 'a')
      .replace(/ć/g, 'c')
      .replace(/ę/g, 'e')
      .replace(/ń/g, 'n')
      .replace(/ó/g, 'o')
      .replace(/ś/g, 's')
      .replace(/ź/g, 'z')
      .replace(/ż/g, 'z')
      .replace(/\s+/g, ' ')
      .trim();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl || !this.control) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl || !this.control) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
    this.onTouched();
  }

  clearValue(): void {
    if (!this.control) {
      return;
    }

    const emptyValue = this.multiple ? [] : '';

    this.control.setValue(emptyValue);
    this.value = emptyValue;
    this.filterControl.setValue('');
    this.onChange(emptyValue);
    this.onTouched();
  }

  hasValue(): boolean {
    if (!this.control) {
      return false;
    }

    const value = this.control.value;

    if (this.multiple) {
      return Array.isArray(value) && value.length > 0;
    }

    return value !== null && value !== undefined && value !== '';
  }

  compareFacility(a: FacilityDobryAgent | string, b: FacilityDobryAgent | string): boolean {
    const aUuid = typeof a === 'string' ? a : a?.uuid;
    const bUuid = typeof b === 'string' ? b : b?.uuid;

    return aUuid === bUuid;
  }

  getAddressLabel(facility: FacilityDobryAgent): string {
    if (!facility) {
      return '';
    }
    if ((facility as any).address?.full) {
      return (facility as any).address?.full;
    }

    const address = (facility as any).address;

    if (address) {
      return [
        [address?.street, address?.houseNumber].filter(Boolean).join(' '),
        [address?.zipCode, address?.city].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ');
    }

    return [
      [facility.address?.street, facility?.address?.houseNumber].filter(Boolean).join(' '),
      [facility.address?.zipCode, (facility as any).city?.name || facility?.address?.city].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ');
  }
}
